import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { STORAGE_KEYS } from '@constants/index';
import { authApi, type AuthSession } from '@services/api/auth';
import { installAuthRefresh } from '@services/http/client';
import type { AuthUser } from '@app-types/index';

interface AuthApi {
  user: AuthUser | null;
  loading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<AuthUser>;
  register: (payload: {
    email: string;
    password1: string;
    password2: string;
    name: string;
  }) => Promise<void>;
  loginWithGoogle: (idToken: string) => Promise<AuthUser>;
  logout: () => Promise<void>;
  refreshMe: () => Promise<AuthUser | null>;
  updateProfile: (payload: { name?: string; defaultForwardTo?: string }) => Promise<AuthUser>;
  completeOnboarding: (payload: { name?: string; defaultForwardTo?: string }) => Promise<AuthUser>;
}

const AuthContext = createContext<AuthApi | null>(null);

// ---------------------------------------------------------------------------
// Persistence helpers — all touch localStorage, all defensive against the
// `localStorage` being unavailable (private mode, SSR, etc.).
// ---------------------------------------------------------------------------
const readUser = (): AuthUser | null => {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.authUser);
    return raw ? (JSON.parse(raw) as AuthUser) : null;
  } catch {
    return null;
  }
};

const writeSession = (session: AuthSession): void => {
  try {
    localStorage.setItem(STORAGE_KEYS.authToken, session.tokens.access);
    localStorage.setItem(STORAGE_KEYS.authRefresh, session.tokens.refresh);
    localStorage.setItem(STORAGE_KEYS.authUser, JSON.stringify(session.user));
  } catch {
    /* swallow — auth still works in-memory for this session */
  }
};

const writeUser = (user: AuthUser): void => {
  try {
    localStorage.setItem(STORAGE_KEYS.authUser, JSON.stringify(user));
  } catch {
    /* ignored */
  }
};

const clearSession = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEYS.authToken);
    localStorage.removeItem(STORAGE_KEYS.authRefresh);
    localStorage.removeItem(STORAGE_KEYS.authUser);
  } catch {
    /* ignored */
  }
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(() => readUser());
  const [loading, setLoading] = useState<boolean>(() => {
    // If we have neither token nor cached user, there's nothing to verify —
    // skip the loading splash entirely and render the public routes.
    return Boolean(localStorage.getItem(STORAGE_KEYS.authToken));
  });

  // Latest user in a ref so callbacks closing over it stay fresh without
  // re-binding the http refresh function on every render.
  const userRef = useRef(user);
  useEffect(() => {
    userRef.current = user;
  }, [user]);

  // -----------------------------------------------------------------------
  // Refresh: ask the backend for a new access token using the refresh token.
  // Returns the new access token (and persists it) or null if refresh fails.
  // -----------------------------------------------------------------------
  const tryRefresh = useCallback(async (): Promise<string | null> => {
    const refresh = localStorage.getItem(STORAGE_KEYS.authRefresh);
    if (!refresh) return null;
    try {
      const res = await authApi.refresh(refresh);
      localStorage.setItem(STORAGE_KEYS.authToken, res.access);
      if (res.refresh) {
        // BLACKLIST_AFTER_ROTATION + ROTATE_REFRESH_TOKENS is on, so each
        // refresh response includes a new refresh token; persist it.
        localStorage.setItem(STORAGE_KEYS.authRefresh, res.refresh);
      }
      return res.access;
    } catch {
      // Refresh failed (expired, blacklisted, server down). Clear local
      // state — the next protected route render will redirect to /login.
      clearSession();
      setUser(null);
      return null;
    }
  }, []);

  // Install the refresh hook on the shared http client. Done once on mount
  // (the ref-based access keeps the closure stable).
  useEffect(() => {
    installAuthRefresh(tryRefresh);
    return () => installAuthRefresh(null);
  }, [tryRefresh]);

  // -----------------------------------------------------------------------
  // Boot: if we have a token cached locally, verify the session by hitting
  // /me. Cached user shows up immediately; the network call corrects it.
  // -----------------------------------------------------------------------
  useEffect(() => {
    const token = localStorage.getItem(STORAGE_KEYS.authToken);
    if (!token) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const fresh = await authApi.getMe();
        if (cancelled) return;
        setUser(fresh);
        writeUser(fresh);
      } catch {
        // The http client already tried a refresh once. If we're here,
        // refresh either wasn't possible or also failed — log out cleanly.
        if (cancelled) return;
        clearSession();
        setUser(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // -----------------------------------------------------------------------
  // Action handlers
  // -----------------------------------------------------------------------
  const handleSession = useCallback((session: AuthSession): AuthUser => {
    writeSession(session);
    setUser(session.user);
    return session.user;
  }, []);

  const login = useCallback(
    async (email: string, password: string): Promise<AuthUser> => {
      const session = await authApi.login(email, password);
      return handleSession(session);
    },
    [handleSession],
  );

  const loginWithGoogle = useCallback(
    async (idToken: string): Promise<AuthUser> => {
      const session = await authApi.googleLogin(idToken);
      return handleSession(session);
    },
    [handleSession],
  );

  const register = useCallback(
    async (payload: {
      email: string;
      password1: string;
      password2: string;
      name: string;
    }): Promise<void> => {
      await authApi.register(payload);
    },
    [],
  );

  const logout = useCallback(async (): Promise<void> => {
    const refresh = localStorage.getItem(STORAGE_KEYS.authRefresh);
    // Best-effort server-side blacklist; we always clear local state.
    try {
      await authApi.logout(refresh);
    } catch {
      /* ignore — token may already be invalid */
    }
    clearSession();
    setUser(null);
  }, []);

  const refreshMe = useCallback(async (): Promise<AuthUser | null> => {
    try {
      const fresh = await authApi.getMe();
      setUser(fresh);
      writeUser(fresh);
      return fresh;
    } catch {
      return null;
    }
  }, []);

  const updateProfile = useCallback(
    async (payload: { name?: string; defaultForwardTo?: string }): Promise<AuthUser> => {
      const updated = await authApi.patchMe(payload);
      setUser(updated);
      writeUser(updated);
      return updated;
    },
    [],
  );

  const completeOnboarding = useCallback(
    async (payload: { name?: string; defaultForwardTo?: string }): Promise<AuthUser> => {
      const updated = await authApi.completeOnboarding(payload);
      setUser(updated);
      writeUser(updated);
      return updated;
    },
    [],
  );

  const value = useMemo<AuthApi>(
    () => ({
      user,
      loading,
      isAuthenticated: Boolean(user),
      login,
      register,
      loginWithGoogle,
      logout,
      refreshMe,
      updateProfile,
      completeOnboarding,
    }),
    [
      user,
      loading,
      login,
      register,
      loginWithGoogle,
      logout,
      refreshMe,
      updateProfile,
      completeOnboarding,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthApi {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an <AuthProvider />');
  return ctx;
}
